<template>
  <div @keyup.ctrl.alt.f="format">
    <div class="card mb-3">
      <div class="card-body">
        <h5 class="card-title">Guided editor</h5>
        <small class="text-muted d-block mb-2">
          Use common fields here. Raw JSON editing remains available below.
        </small>

        <div v-if="guidedEditorError" class="alert alert-warning mb-0" role="alert">
          {{ guidedEditorError }}
        </div>

        <div v-else class="row">
          <div class="col-12 mb-3">
            <div class="alert alert-info mb-0" role="note">
              <div>
                <strong>
                  Component help ({{ guidedFilter?.component || "CheckboxFilter" }})
                </strong>
              </div>
              <div class="mt-1">{{ guidedComponentHelp.description }}</div>
              <div class="mt-2">
                <strong>Required:</strong>
                {{ guidedComponentHelp.required.join(", ") }}
              </div>
              <div class="mt-1">
                <strong>Recommended:</strong>
                {{ guidedComponentHelp.recommended.join(", ") }}
              </div>
              <div class="mt-1" v-if="guidedComponentHelp.notes.length > 0">
                <strong>Notes:</strong>
                {{ guidedComponentHelp.notes.join(" ") }}
              </div>
            </div>
          </div>

          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-facet-identifier">Facet identifier</label>
            <input
              id="guided-facet-identifier"
              type="text"
              class="form-control"
              :value="guidedFilter?.facetIdentifier || ''"
              @input="updateGuidedField('facetIdentifier', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-facet-title">Facet title</label>
            <input
              id="guided-facet-title"
              type="text"
              class="form-control"
              :value="guidedFilter?.facetTitle || ''"
              @input="updateGuidedField('facetTitle', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-component">Component</label>
            <select
              id="guided-component"
              class="form-control"
              :value="guidedFilter?.component || 'CheckboxFilter'"
              @change="updateGuidedField('component', $event.target.value)"
            >
              <option value="CheckboxFilter">CheckboxFilter</option>
              <option value="OntologyFilter">OntologyFilter</option>
              <option value="ToggleFilter">ToggleFilter</option>
              <option value="ServiceFilter">ServiceFilter</option>
              <option value="StringFilter">StringFilter</option>
            </select>
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-apply-to-column">Apply to column</label>
            <input
              id="guided-apply-to-column"
              type="text"
              class="form-control"
              :value="guidedFilter?.applyToColumn || ''"
              @input="updateGuidedField('applyToColumn', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-source-schema">Source schema</label>
            <input
              id="guided-source-schema"
              type="text"
              class="form-control"
              :value="guidedFilter?.sourceSchema || ''"
              @input="updateGuidedField('sourceSchema', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-source-table">Source table</label>
            <input
              id="guided-source-table"
              type="text"
              class="form-control"
              :value="guidedFilter?.sourceTable || ''"
              @input="updateGuidedField('sourceTable', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-label-attribute">Label attribute</label>
            <input
              id="guided-label-attribute"
              type="text"
              class="form-control"
              :value="guidedFilter?.filterLabelAttribute || ''"
              @input="updateGuidedField('filterLabelAttribute', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-value-attribute">Value attribute</label>
            <input
              id="guided-value-attribute"
              type="text"
              class="form-control"
              :value="guidedFilter?.filterValueAttribute || ''"
              @input="updateGuidedField('filterValueAttribute', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-sort-column">Sort column</label>
            <input
              id="guided-sort-column"
              type="text"
              class="form-control"
              :value="guidedFilter?.sortColumn || ''"
              @input="updateGuidedField('sortColumn', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-sort-direction">Sort direction</label>
            <select
              id="guided-sort-direction"
              class="form-control"
              :value="(guidedFilter?.sortDirection || 'asc').toLowerCase()"
              @change="updateGuidedField('sortDirection', $event.target.value)"
            >
              <option value="asc">asc</option>
              <option value="desc">desc</option>
            </select>
          </div>
          <div class="col-md-6 mb-2">
            <label class="mb-1" for="guided-ontology-identifiers">Ontology identifiers (comma separated)</label>
            <input
              id="guided-ontology-identifiers"
              type="text"
              class="form-control"
              :value="(guidedFilter?.ontologyIdentifiers || []).join(', ')"
              @input="updateGuidedArrayField('ontologyIdentifiers', $event.target.value)"
            />
          </div>
          <div class="col-md-6 mb-2 d-flex align-items-end">
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="showFacetCheckbox"
                :checked="guidedFilter?.showFacet === true"
                @change="updateGuidedField('showFacet', $event.target.checked)"
              />
              <label class="form-check-label" for="showFacetCheckbox">Show facet</label>
            </div>
          </div>

          <template v-if="guidedFilter?.component === 'ToggleFilter'">
            <div class="col-md-6 mb-2">
              <label class="mb-1" for="guided-true-option-text">Toggle true option text</label>
              <input
                id="guided-true-option-text"
                type="text"
                class="form-control"
                :value="guidedFilter?.trueOption?.text || ''"
                @input="updateGuidedField('trueOption.text', $event.target.value)"
              />
            </div>
            <div class="col-md-6 mb-2 d-flex align-items-end">
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="trueOptionValueCheckbox"
                  :checked="guidedFilter?.trueOption?.value === true"
                  @change="updateGuidedField('trueOption.value', $event.target.checked)"
                />
                <label class="form-check-label" for="trueOptionValueCheckbox">Toggle true option value</label>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div ref="filter-editor" class="filter-editor" @keyup="dirty = true"></div>

    <button class="btn btn-info mt-3" :disabled="!dirty" @click="apply">
      Apply changes to {{ title }}
    </button>
    <button class="btn btn-secondary ml-3 mt-3" @click="testSourceConfig">
      Test source
    </button>
    <button class="btn btn-danger ml-3 mt-3" @click="deleteFilter">
      Delete {{ title }}
    </button>

    <div v-if="sourceTestMessage" class="alert mt-3 mb-0" :class="sourceTestAlertClass" role="alert">
      {{ sourceTestMessage }}
    </div>
  </div>
</template>

<script>
import * as monaco from "monaco-editor";
import { toRaw } from "vue";
import { testFilterSource } from "../../filter-config/testFilterSource";
import {
  formatFilterJson,
  getComponentHelp,
  parseCommaSeparatedValues,
  parseEditorFilter,
  setGuidedFilterValue,
} from "./filterEditorGuided";

export default {
  props: {
    config: {
      type: String,
      required: true,
    },
    filterIndex: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    graphqlEndpoint: {
      type: String,
      required: false,
      default: "graphql",
    },
  },
  methods: {
    format() {
      if (this.filterEditor && this.filterEditor.getAction) {
        this.filterEditor.getAction("editor.action.formatDocument").run();
      }
    },
    apply() {
      const newConfig = this.configObject;
      newConfig.filterFacets[this.filterIndex] = JSON.parse(
        toRaw(this.filterEditor).getValue()
      );
      this.$emit("filterUpdate", JSON.stringify(newConfig));
    },
    deleteFilter() {
      this.$emit("delete");
    },
    updateGuidedField(path, value) {
      if (!this.guidedFilter) {
        return;
      }

      this.guidedFilter = setGuidedFilterValue(this.guidedFilter, path, value);
      this.replaceEditorValue(formatFilterJson(this.guidedFilter));
    },
    updateGuidedArrayField(path, csvInput) {
      this.updateGuidedField(path, parseCommaSeparatedValues(csvInput));
    },
    replaceEditorValue(newValue) {
      if (!this.filterEditor?.getModel) {
        return;
      }

      this.filterEditor.getModel().setValue(newValue);
      this.dirty = true;
      this.syncGuidedEditorFromJson();
    },
    syncGuidedEditorFromJson() {
      if (!this.filterEditor || !this.filterEditor.getValue) {
        this.guidedFilter = null;
        this.guidedEditorError = "";
        return;
      }

      const parsed = parseEditorFilter(this.filterEditor.getValue());
      this.guidedFilter = parsed.filter;
      this.guidedEditorError = parsed.error;
    },
    async testSourceConfig() {
      let parsedFilter;

      try {
        parsedFilter = JSON.parse(toRaw(this.filterEditor).getValue());
      } catch (error) {
        this.sourceTestMessage = "Current filter JSON is invalid. Fix formatting before testing source.";
        this.sourceTestState = "error";
        return;
      }

      this.sourceTestMessage = "Testing source connectivity...";
      this.sourceTestState = "pending";

      const result = await testFilterSource(parsedFilter, this.graphqlEndpoint);

      if (result.success) {
        this.sourceTestState = "success";
        this.sourceTestMessage = `${result.message} Found ${result.count ?? 0} row(s).`;
        return;
      }

      this.sourceTestState = "error";
      this.sourceTestMessage = result.message;
    },
  },
  computed: {
    configObject() {
      return JSON.parse(this.config);
    },
    filter() {
      return JSON.stringify(this.configObject.filterFacets[this.filterIndex]);
    },
    sourceTestAlertClass() {
      if (this.sourceTestState === "success") {
        return "alert-success";
      }

      if (this.sourceTestState === "error") {
        return "alert-danger";
      }

      return "alert-secondary";
    },
    guidedComponentHelp() {
      return getComponentHelp(this.guidedFilter?.component || "CheckboxFilter");
    },
  },
  data() {
    return {
      dirty: false,
      filterEditor: {},
      guidedFilter: null,
      guidedEditorError: "",
      sourceTestState: "idle",
      sourceTestMessage: "",
    };
  },
  destroyed() {
    this.dirty = false;
    this.filterEditor.dispose();
  },
  watch: {
    config() {
      this.dirty = false;
      this.sourceTestState = "idle";
      this.sourceTestMessage = "";
      if (this.filterEditor.dispose) {
        this.filterEditor.getModel().setValue(this.filter);
        this.syncGuidedEditorFromJson();
        this.format();
      }
    },
  },
  mounted() {
    this.filterEditor = monaco.editor.create(this.$refs["filter-editor"], {
      automaticLayout: true,
      value: this.filter,
      language: "json",
    });

    this.syncGuidedEditorFromJson();
    this.filterEditor.onDidChangeModelContent(() => {
      this.syncGuidedEditorFromJson();
    });

    const formatTimer = setTimeout(() => {
      this.format();
      clearTimeout(formatTimer);
    }, 400);
  },
};
</script>

<style scoped>
.filter-editor {
  height: 100%;
  width: 100%;
}
</style>
